import { requireBillingOpenAnalystApiAccessToken } from "@/services/billing-auth"
import { openAnalystBillingApiBaseUrl } from "@/utils/openanalyst-hosts"

const BILLING_API_BASE_URL = openAnalystBillingApiBaseUrl()
let configuredFetch: typeof fetch | null = null

export function configureBillingFetch(fetcher: typeof fetch | null) {
  configuredFetch = fetcher
}

export type PurchaseRecord = {
  id: string
  paymentId: string
  amountPaid: number
  currency: string
  creditsAdded: number
  creditsRemaining: number
  validFrom: string
  validUntil: string
  daysRemaining: number
  status: "active" | "exhausted" | "expired"
  createdAt: string
}

export type PurchasesResponse = {
  success: boolean
  purchases?: PurchaseRecord[]
  count?: number
  totalCount?: number
  error?: { code: string; message: string }
}

export type CheckoutResponse = {
  success: boolean
  url?: string
  error?: { code: string; message: string }
}

export const PRICE_PER_CREDIT = 0.01
export const CREDITS_PER_DOLLAR = Math.floor(1 / PRICE_PER_CREDIT)
export const MIN_PURCHASE_AMOUNT = 5
export const MAX_PURCHASE_AMOUNT = 5000
export const PURCHASE_STEP = 5

async function authHeaders(): Promise<HeadersInit> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${await requireBillingOpenAnalystApiAccessToken()}`,
  }
}

export async function fetchPurchases(): Promise<PurchaseRecord[]> {
  const response = await (configuredFetch ?? fetch)(`${BILLING_API_BASE_URL}/credits/purchases`, {
    method: "GET",
    headers: await authHeaders(),
  })

  if (!response.ok) return []

  const data = (await response.json().catch(() => undefined)) as PurchasesResponse | undefined
  if (!data?.success || !data.purchases) return []

  return data.purchases
}

export async function createCheckout(amount: number): Promise<string> {
  if (amount < MIN_PURCHASE_AMOUNT || amount > MAX_PURCHASE_AMOUNT) {
    throw new Error(`Amount must be between $${MIN_PURCHASE_AMOUNT} and $${MAX_PURCHASE_AMOUNT}`)
  }
  if (amount % PURCHASE_STEP !== 0) {
    throw new Error(`Amount must be a multiple of $${PURCHASE_STEP}`)
  }

  const response = await (configuredFetch ?? fetch)(`${BILLING_API_BASE_URL}/billing/create-checkout`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ customAmount: amount }),
  })

  if (!response.ok) {
    const data = (await response.json().catch(() => undefined)) as CheckoutResponse | undefined
    throw new Error(data?.error?.message || "Could not start checkout")
  }

  const data = (await response.json().catch(() => undefined)) as CheckoutResponse | undefined
  if (!data?.success || !data.url) throw new Error("No checkout URL returned")

  return data.url
}
